// DTOs du module KYC — miroir exact des contrats backend (KycController).
// Toutes les requêtes/réponses sont chiffrées AES au niveau du composant
// appelant (voir src/Components/Forms/Kyc/kycCrypto.ts), pas ici. Les routes
// elles-mêmes vivent dans Data.json (Data.ApiUrl.Kyc*), comme partout
// ailleurs dans l'appli.

import type ParamsPicklist from '../Types/ParamsPicklist';

export type TypeModificationKyc =
  // Type générique utilisé par toute nouvelle soumission depuis que le
  // formulaire ne fait plus choisir de catégorie : une demande peut mélanger
  // champs client et compte, le backend force ce type quoi qu'on envoie.
  | 'MODIFICATION_CLIENT_COMPTE'
  // Anciens types : encore acceptés/retournés pour les demandes déjà en
  // attente ou déjà traitées avant ce changement, plus utilisables en écriture.
  | 'TELEPHONE'
  | 'NOM_COMPTE'
  | 'TYPE_SIGNATURE'
  | 'IDENTITE_CLIENT'
  | 'COORDONNEES_CLIENT'
  | 'PIECE_IDENTITE'
  // Groupes purement locaux (jamais envoyés comme TypeModification — tout
  // part en MODIFICATION_CLIENT_COMPTE) ajoutés avec les 28 nouveaux champs
  // client demandés par le backend (2026-09-23), pour organiser l'affichage.
  | 'PROFIL_CLIENT'
  | 'EMPLOYEUR_FAMILLE'
  | 'PHOTO_SIGNATURE'
  // Téléphone du COMPTE (2026-09-28) — à ne pas confondre avec TELEPHONE
  // (TELCLI/TEL2, téléphones du client). Groupe purement local, comme
  // PROFIL_CLIENT etc.
  | 'TELEPHONE_COMPTE';

export type ChampKyc =
  | 'TELCLI' | 'TEL2' | 'NOMCPT' | 'TYPSIGNATURE'
  | 'NOMCLI' | 'PRECLI' | 'DATNAI' | 'SITFAM' | 'PROFESION'
  | 'ADRPOSTAL' | 'RUECLI' | 'NOMQTIER' | 'VILCLI' | 'BPCLI' | 'EMAILCLI'
  | 'NUMCNI' | 'TYPEPIECE' | 'DATEDELCNI' | 'NIU'
  // 28 nouveaux champs client (2026-09-23) — reprennent exactement les codes
  // donnés par le backend, qui reprennent eux-mêmes les colonnes déjà
  // utilisées par l'écran CLIENT PHYSIQUE existant (Tab1.tsx/Tab2.tsx).
  // Picklist (13, voir CHAMPS_SELECT_KYC) :
  | 'TITCLI' | 'CLARIS' | 'CDPAYS' | 'CDERES' | 'RESIDE' | 'CDGEST' | 'REGMAT'
  | 'CDENAE' | 'CATPRO' | 'ETHNIE' | 'CDENUI' | 'INSTRU' | 'TYPADR'
  // Choix fixe (2, radio 2 options) :
  | 'CLSEXE' | 'ACTIONAIRE'
  // Texte/numérique libre (13) :
  | 'CODPROSPECT' | 'LIENAI' | 'FAXCLI' | 'ADRDOM' | 'NBRENFANT' | 'NBRPERSCHARGE'
  | 'NOMEMP' | 'MATEMP' | 'ADREMP' | 'NOCNPS' | 'NOMPERE' | 'NOMMERE' | 'REVENUCLI'
  // Photo/signature (2026-09-23) — ciblent RELATION.photo/RELATION.signature
  // (la fiche auto-générée du client, IdClient=IdRel=NUMCLI), pas CLIENT.
  // Valeur = data URI complète ("data:image/...;base64,...", stockée telle
  // quelle côté serveur, sans décodage — donc à envoyer avec ce préfixe).
  | 'PHOTO' | 'SIGNATURE'
  // Type de signature du compte (2026-09-25) — formule ET/OU entre jusqu'à 5
  // signataires (composant partagé Tsignature.tsx, déjà utilisé ailleurs dans
  // l'appli, ex. /situationclient/vu360). SIGNx = CODREL d'un signataire (ou
  // "" pour vider l'emplacement), TSIGNx = opérateur entre deux emplacements
  // consécutifs, uniquement "/" (ou) ou "&" (et) — contrat confirmé backend.
  | 'SIGN1' | 'SIGN2' | 'SIGN3' | 'SIGN4' | 'SIGN5'
  | 'TSIGN1' | 'TSIGN2' | 'TSIGN3' | 'TSIGN4'
  // Téléphone du COMPTE (2026-09-28), distinct de TELCLI/TEL2 (téléphones du
  // client) — chaîne de chiffres uniquement, stockée en int côté base (un
  // texte non numérique est silencieusement ignoré à la validation backend).
  | 'TELMOBIL';

// DATNAI/DATEDELCNI attendent une date au format yyyy-mm-dd (cf. correctif
// backend TRY_CONVERT — envoyer ce format évite tout souci de locale SQL).
export const CHAMPS_DATE_KYC: ChampKyc[] = ['DATNAI', 'DATEDELCNI'];

export type TypeModificationRelationKyc =
  | 'SIGNATAIRE_AJOUT'
  | 'SIGNATAIRE_RETRAIT'
  | 'PROCURATION_AJOUT'
  | 'PROCURATION_RETRAIT'
  // Modification de l'identité propre d'un signataire/mandataire déjà
  // existant (2026-10-08) — PAS un ajout/retrait de lien compte, un vrai
  // changement de ses champs (nom, téléphone, CNI...). Endpoint dédié
  // POST /Kyc/DemandeModificationIdentite (voir DemandeKycIdentiteDto),
  // mais réutilise ce même type pour TypeRelation/TypeModification côté
  // demande, donc RELATION_ACTION_INFO (ValidationPanel) les couvre aussi.
  | 'SIGNATAIRE_MODIFICATION'
  | 'PROCURATION_MODIFICATION';

export type StatutValidationKyc = 'VALIDE' | 'REJETE';

// Modification de l'identité d'un signataire (préfixe REL_) ou d'un
// mandataire/procuration (préfixe PROC_) déjà lié à un compte — pas un ajout/
// retrait de lien, un changement de ses propres champs (nom, téléphone,
// CNI...). Contrat confirmé par le backend (2026-10-08).
export type ChampIdentiteSignataireKyc =
  | 'REL_NOMREL' | 'REL_PRENOM' | 'REL_TITREL' | 'REL_SEXE' | 'REL_LIEUNAIS' | 'REL_DATENAIS'
  | 'REL_CNI' | 'REL_DATEXPIRCNI' | 'REL_ADRESREL' | 'REL_TELREL' | 'REL_FILIATION' | 'REL_AGCCPT'
  | 'REL_PHOTO' | 'REL_SIGNATURE';

export type ChampIdentiteProcurationKyc =
  | 'PROC_NOM' | 'PROC_PRENOM' | 'PROC_TITRE' | 'PROC_SEXE' | 'PROC_LIEUNAIS' | 'PROC_DATENAIS'
  | 'PROC_CNI' | 'PROC_DATEXPIRCNI' | 'PROC_TELPROCUR' | 'PROC_ADRESPROCUR' | 'PROC_FILIATION' | 'PROC_AGCCPT'
  | 'PROC_PHOTO' | 'PROC_SIGNATURE' | 'PROC_AUTORITE' | 'PROC_DATDEBUT' | 'PROC_DATFIN' | 'PROC_DATEXPIRPROCUR'
  | 'PROC_PIECEJ1' | 'PROC_PIECEJ2' | 'PROC_PLAFOND';

export type ChampIdentiteKyc = ChampIdentiteSignataireKyc | ChampIdentiteProcurationKyc;

// REL_AGCCPT/PROC_AGCCPT (code agence) volontairement exclus : code interne
// sans intérêt pour l'identité d'une personne, ne doit pas s'afficher ici
// (retour utilisateur 2026-10-10) — donc jamais modifiable via ce flow non plus.
export const CHAMPS_IDENTITE_SIGNATAIRE: ChampIdentiteSignataireKyc[] = [
  'REL_NOMREL', 'REL_PRENOM', 'REL_TITREL', 'REL_SEXE', 'REL_LIEUNAIS', 'REL_DATENAIS',
  'REL_CNI', 'REL_DATEXPIRCNI', 'REL_ADRESREL', 'REL_TELREL', 'REL_FILIATION',
  'REL_PHOTO', 'REL_SIGNATURE',
];
export const CHAMPS_IDENTITE_PROCURATION: ChampIdentiteProcurationKyc[] = [
  'PROC_NOM', 'PROC_PRENOM', 'PROC_TITRE', 'PROC_SEXE', 'PROC_LIEUNAIS', 'PROC_DATENAIS',
  'PROC_CNI', 'PROC_DATEXPIRCNI', 'PROC_TELPROCUR', 'PROC_ADRESPROCUR', 'PROC_FILIATION',
  'PROC_PHOTO', 'PROC_SIGNATURE', 'PROC_AUTORITE', 'PROC_DATDEBUT', 'PROC_DATFIN', 'PROC_DATEXPIRPROCUR',
  'PROC_PIECEJ1', 'PROC_PIECEJ2', 'PROC_PLAFOND',
];

// Regroupement par thème pour l'affichage (KycIdentiteRelationPage,
// KycModifierIdentiteModal) — même principe que GROUPES_CLIENT/GROUPES_COMPTE
// dans KycPage. Exactement 2 blocs texte (gauche/droite) puis, en dessous,
// les blocs "média" (Photo & Signature, Pièces jointes si applicable) — pas
// un bloc par thème comme un premier essai, trop morcelé (retour utilisateur
// 2026-10-10 : "tout les autres informations dans seulement deux blocs").
// Purement pour l'affichage : le diff envoyé au backend (onSubmitModification)
// reste basé sur CHAMPS_IDENTITE_SIGNATAIRE/CHAMPS_IDENTITE_PROCURATION
// (plats), inchangé.
export interface GroupeChampIdentiteKyc {
  titre: string;
  champs: ChampIdentiteKyc[];
}

export const GROUPES_CHAMPS_IDENTITE_SIGNATAIRE: GroupeChampIdentiteKyc[] = [
  { titre: 'Identité & naissance', champs: ['REL_NOMREL', 'REL_PRENOM', 'REL_TITREL', 'REL_SEXE', 'REL_LIEUNAIS', 'REL_DATENAIS'] },
  { titre: 'Coordonnées & pièce d\'identité', champs: ['REL_CNI', 'REL_DATEXPIRCNI', 'REL_ADRESREL', 'REL_TELREL', 'REL_FILIATION'] },
  { titre: 'Photo & Signature', champs: ['REL_PHOTO', 'REL_SIGNATURE'] },
];

export const GROUPES_CHAMPS_IDENTITE_PROCURATION: GroupeChampIdentiteKyc[] = [
  { titre: 'Identité & naissance', champs: ['PROC_NOM', 'PROC_PRENOM', 'PROC_TITRE', 'PROC_SEXE', 'PROC_LIEUNAIS', 'PROC_DATENAIS', 'PROC_CNI', 'PROC_DATEXPIRCNI'] },
  { titre: 'Coordonnées & procuration', champs: ['PROC_TELPROCUR', 'PROC_ADRESPROCUR', 'PROC_FILIATION', 'PROC_AUTORITE', 'PROC_DATDEBUT', 'PROC_DATFIN', 'PROC_DATEXPIRPROCUR', 'PROC_PLAFOND'] },
  { titre: 'Photo & Signature', champs: ['PROC_PHOTO', 'PROC_SIGNATURE'] },
  { titre: 'Pièces jointes', champs: ['PROC_PIECEJ1', 'PROC_PIECEJ2'] },
];

export const LABELS_CHAMP_IDENTITE_KYC: Record<ChampIdentiteKyc, string> = {
  REL_NOMREL: 'Nom', REL_PRENOM: 'Prénom', REL_TITREL: 'Titre', REL_SEXE: 'Sexe',
  REL_LIEUNAIS: 'Lieu de naissance', REL_DATENAIS: 'Date de naissance', REL_CNI: 'N° CNI',
  REL_DATEXPIRCNI: 'Date expiration CNI', REL_ADRESREL: 'Adresse', REL_TELREL: 'Téléphone',
  REL_FILIATION: 'Filiation', REL_AGCCPT: 'Agence', REL_PHOTO: 'Photo', REL_SIGNATURE: 'Signature',
  PROC_NOM: 'Nom', PROC_PRENOM: 'Prénom', PROC_TITRE: 'Titre', PROC_SEXE: 'Sexe',
  PROC_LIEUNAIS: 'Lieu de naissance', PROC_DATENAIS: 'Date de naissance', PROC_CNI: 'N° CNI',
  PROC_DATEXPIRCNI: 'Date expiration CNI', PROC_TELPROCUR: 'Téléphone', PROC_ADRESPROCUR: 'Adresse',
  PROC_FILIATION: 'Filiation', PROC_AGCCPT: 'Agence', PROC_PHOTO: 'Photo', PROC_SIGNATURE: 'Signature',
  PROC_AUTORITE: 'Autorité', PROC_DATDEBUT: 'Date début', PROC_DATFIN: 'Date fin',
  PROC_DATEXPIRPROCUR: 'Date expiration procuration', PROC_PIECEJ1: 'Pièce jointe 1',
  PROC_PIECEJ2: 'Pièce jointe 2', PROC_PLAFOND: 'Plafond',
};

export const CHAMPS_DATE_IDENTITE_KYC: ChampIdentiteKyc[] = [
  'REL_DATENAIS', 'REL_DATEXPIRCNI', 'PROC_DATENAIS', 'PROC_DATEXPIRCNI', 'PROC_DATDEBUT', 'PROC_DATFIN', 'PROC_DATEXPIRPROCUR',
];
export const CHAMPS_IMAGE_IDENTITE_KYC: ChampIdentiteKyc[] = ['REL_PHOTO', 'REL_SIGNATURE', 'PROC_PHOTO', 'PROC_SIGNATURE'];

// PROC_PIECEJ1/2 (pièces jointes de la procuration) stockent aussi une data
// URI, mais pas forcément une image (peut être un PDF scanné) — type
// 'document' distinct de 'image' dans EditableValueField : accepte PDF/Word à
// l'upload, et bascule entre aperçu <Image> ou lien "Voir le document" selon
// le MIME réel de la data URI stockée.
export const CHAMPS_DOCUMENT_IDENTITE_KYC: ChampIdentiteKyc[] = ['PROC_PIECEJ1', 'PROC_PIECEJ2'];

// REL_SEXE/PROC_SEXE : même choix fixe à 2 options (radio) que CLSEXE pour le
// client (cf. CHAMPS_RADIO_KYC) — pas une saisie libre, pour éviter d'envoyer
// n'importe quel texte comme sexe.
export const CHAMPS_RADIO_IDENTITE_KYC: Partial<Record<ChampIdentiteKyc, { value1: string; libelle1: string; value2: string; libelle2: string }>> = {
  REL_SEXE: { value1: 'M', libelle1: 'Masculin', value2: 'F', libelle2: 'Féminin' },
  PROC_SEXE: { value1: 'M', libelle1: 'Masculin', value2: 'F', libelle2: 'Féminin' },
};

export interface KycIdentiteDetailDto {
  Champ: ChampIdentiteKyc;
  NouvelleValeur: string;
}

// POST /Kyc/DemandeModificationIdentite — contrat séparé de DemandeKycDto
// (pas de NUMCPT, pas de TypeModification générique) : une fiche relation/
// procuration n'appartient pas à un seul compte en particulier.
export interface DemandeKycIdentiteDto {
  NUMCLI: string;
  TypeRelation: 'SIGNATAIRE_MODIFICATION' | 'PROCURATION_MODIFICATION';
  CodRel: string;
  EnregistrePar: string;
  Details: KycIdentiteDetailDto[];
}

export interface KycDetailDto {
  Champ: ChampKyc;
  AncienneValeur?: string | null;
  NouvelleValeur: string;
}

// Bloc optionnel permettant de rattacher un ajout/retrait de signataire ou
// procuration à LA MÊME demande qu'une modification de champs (un seul
// IdDemande pour tout). Absent = comportement inchangé (demande de champs
// seule). Pour un ajout/retrait seul, sans aucun champ modifié, envoyer
// Details: [] avec ce bloc rempli fonctionne aussi.
export interface DemandeKycRelationDto {
  TypeRelation: TypeModificationRelationKyc;
  CodRel: string;
  Plafond?: number;
}

export interface DemandeKycDto {
  NUMCLI: string;
  NUMCPT?: string | null;
  // Optionnel depuis l'ajout de MODIFICATION_CLIENT_COMPTE : le backend force
  // la valeur de toute façon, mais on l'envoie explicitement pour la clarté.
  TypeModification?: TypeModificationKyc;
  EnregistrePar: string;
  Details: KycDetailDto[];
  Relation?: DemandeKycRelationDto;
}

// Un signataire/mandataire n'est jamais créé ici : sa fiche existe déjà
// (tables RELATION/Procuration, alimentées par un autre processus). Le module
// KYC ne fait que lier/délier un compte à une fiche existante (CodRel =
// CODREL pour un signataire, Refprocur pour une procuration), avec un Plafond
// propre au lien (utilisé pour l'ajout uniquement, ignoré au retrait).
export interface DemandeRelationKycDto {
  NUMCLI: string;
  NUMCPT: string;
  TypeModification: TypeModificationRelationKyc;
  EnregistrePar: string;
  CodRel: string;
  Plafond?: number;
}

// GET /Kyc/RelationsClient (header CypherNumcli) — toutes les fiches
// "relation" déjà enregistrées pour ce client, candidates à un ajout comme
// signataire sur un de ses comptes. Le client apparaît lui-même dans cette
// liste (sa propre fiche) et doit être filtré à l'affichage (comparer cni).
export interface RelationClientRow {
  NumRel: string;
  CODREL: string;
  NomRel: string;
  Prenom: string;
  cni?: string;
  Signataire?: string;
  cdetat?: string;
  // Photo/signature de la personne (2026-09-28, demandé pour affichage dans
  // les tableaux Signataires/Procurations) — casse exacte pas encore
  // confirmée par le backend sur cet endpoint spécifique ; lu de façon
  // défensive (getVal, plusieurs casses) et absent sans casser l'affichage.
  Photo?: string;
  Signature?: string;
}

// GET /Kyc/ProcurationsClient (header CypherNumcli) — même principe pour les
// fiches "procuration" du client.
export interface ProcurationClientRow {
  CodProcur: string;
  Refprocur: string;
  nom: string;
  prenom: string;
  cni?: string;
  cdetat?: string;
  Photo?: string;
  Signature?: string;
}

// GET /Kyc/SignatairesCompte (header CypherNumcpt) — signataires actuellement
// liés à CE compte (table de jonction CPTE_RELATION) : sert à l'affichage et
// au choix du retrait.
export interface SignataireCompteRow {
  NUMCPT: string;
  CODREL: string;
  LIMITE: number;
  NomRel: string;
  Prenom: string;
  cni?: string;
  Photo?: string;
  Signature?: string;
}

// GET /Kyc/ProcurationsCompte (header CypherNumcpt) — même principe pour les
// procurations liées à ce compte (table CPTE_PROCU).
export interface ProcurationCompteRow {
  NUMCPT: string;
  CODREL: string;
  LIMITE: number;
  nom: string;
  prenom: string;
  cni?: string;
  Photo?: string;
  Signature?: string;
}

// GET /Relation/GetByCodRel (header CypherCodRel) — fiche identité complète
// d'UN signataire, retrouvée directement par son code (indépendamment du
// client/compte — sp_GetKycAnnuaire, nouveau mode, confirmé par le backend
// 2026-10-09). Result.Table[0] si trouvé ; 404 + Result:null sinon (voir
// fetchOne, kycCrypto.ts, qui remonte le Message "Relation introuvable : XXX").
export interface SignataireIdentiteRow {
  NUMCLI: string;
  CODREL: string;
  NomRel: string;
  Prenom: string;
  TitRel?: string;
  Sexe?: string;
  IdRel?: string;
  LieuNais?: string;
  dateNais?: string;
  cni?: string;
  DatExpirCni?: string;
  adresRel?: string;
  TelRel?: string;
  filiation?: string;
  agccpt?: string;
  cdetat?: string;
  Signataire?: string;
  Photo?: string;
  Signature?: string;
}

// GET /Procuration/GetByRefprocur (header CypherRefprocur) — même principe
// pour UNE procuration, retrouvée par Refprocur.
export interface ProcurationIdentiteRow {
  NUMCLI: string;
  Refprocur: string;
  nom: string;
  prenom: string;
  titre?: string;
  sexe?: string;
  lieuNais?: string;
  dateNais?: string;
  idrel?: string;
  cni?: string;
  TelProcur?: string;
  adresProcur?: string;
  datExpirCni?: string;
  filiation?: string;
  agccpt?: string;
  autorite?: string;
  datDebut?: string;
  DatFin?: string;
  DatExpirProcur?: string;
  Piecej1?: string;
  Piecej2?: string;
  Plafond?: number;
  cdetat?: string;
  Photo?: string;
  Signature?: string;
}

// GET /Kyc/ComptesDuSignataire ou /Kyc/ComptesDeLaProcuration (header
// CypherCodRel, même header pour les 2 routes) — comptes auxquels ce
// signataire/mandataire est déjà rattaché, pour affichage informatif avant
// modification (pas de lien avec un compte précis ici, juste du contexte).
export interface CompteRattacheRow {
  NUMCPT: string;
  CODREL: string;
  LIMITE: number;
  NOMCPT: string;
}

export interface DemandeKycValidationDto {
  IdDemande: number | string;
  StatutValidation: StatutValidationKyc;
  CommentaireValidation?: string;
  ValidePar: string;
}

// POST /Kyc/UploadDocument — Base64File est le contenu brut en base64 (SANS le
// préfixe "data:<mime>;base64,"). Refusé par le backend si la demande est déjà
// validée/rejetée (pièces attachables uniquement avant décision).
export interface DemandeKycDocumentUploadDto {
  IdDemande: number | string;
  NomFichierOriginal: string;
  Extension: string;
  TypeMime: string;
  UploadePar: string;
  Base64File: string;
}

// Ligne renvoyée par GET /Kyc/Documents (header CypherIdDemande) —
// ContenuFichier est le contenu en base64, à reconstituer en data URI côté
// affichage : `data:${TypeMime};base64,${ContenuFichier}`.
export interface DemandeKycDocumentRow {
  IdDocument: number | string;
  IdDemande: number | string;
  NomFichierOriginal: string;
  Extension: string;
  TypeMime: string;
  TailleOctets: number;
  ContenuFichier: string;
  UploadePar: string;
  DateUpload: string;
}

// Forme générique de la réponse déchiffrée, commune à toute l'appli.
export interface KycApiResponse<T = any> {
  StatutCode: number;
  Message: string;
  Result: T;
}

// Ligne de compte générique (via le picklist AnySearch, Table=COMPTES) —
// /Kyc/StatutClient (sp_GetClientStatutEtStats) a été retiré côté backend,
// donc seuls NUMCPT/NOMCPT sont garantis ici.
export interface CompteStatutRow {
  NUMCPT: string;
  NOMCPT: string;
  // Plan comptable (2026-09-28) — absent du picklist générique (2 colonnes
  // seulement), complété par un appel GetCompte par compte (peu nombreux) une
  // fois la liste chargée. lib_IDPLAN = libellé humain, IDPLAN = code brut
  // (repli si le libellé n'est pas fourni pour ce compte).
  IDPLAN?: string;
  lib_IDPLAN?: string;
}

// Forme exacte de Result pour GET /Kyc/DemandesEnAttente. Header CypherNumcli
// optionnel : absent = file globale, présent = filtrée sur ce client. Le
// paramètre de requête ?statut= (EN_ATTENTE par défaut, VALIDE/REJETE/ALL)
// filtre côté backend ; StatutValidation/ValidePar/DateValidation/
// CommentaireValidation sont toujours présents désormais (null si en attente).
export interface DemandeEnAttenteRow {
  IdDemande: number | string;
  NUMCLI: string;
  NOMCLI: string;
  NUMCPT?: string | null;
  TypeModification: string;
  EnregistrePar: string;
  DateCreation: string;
  StatutValidation?: StatutValidationKyc | null;
  ValidePar?: string | null;
  DateValidation?: string | null;
  CommentaireValidation?: string | null;
}

export type FiltreStatutDemandeKyc = 'EN_ATTENTE' | 'VALIDE' | 'REJETE' | 'ALL';

// Détail des champs modifiés — pertinent pour TELEPHONE/NOM_COMPTE/TYPE_SIGNATURE.
export interface DemandeDetailChampRow {
  IdDemande: number | string;
  Champ: string;
  AncienneValeur?: string | null;
  NouvelleValeur: string;
}

// Détail identité de la relation — présent pour les demandes SIGNATAIRE_*/
// PROCURATION_* seules ET pour toute demande combinée (TypeModification=
// MODIFICATION_CLIENT_COMPTE) qui porte aussi un ajout/retrait. TypeRelation
// devait dire QUELLE action de relation appliquer, indépendamment de l'en-
// tête (qui reste MODIFICATION_CLIENT_COMPTE) — annoncé par le backend mais
// absent en pratique de la réponse réelle (vérifié sur demande n°43,
// vu par le frontend : {"CodRel","Plafond","NomRel","Prenom","Cni"}) : pas
// de Rang non plus. Champ conservé côté modèle en attendant que le backend
// le renvoie réellement ; CodRel est le vrai nom de la référence (pas
// NumRelExistant, gardé en repli au cas où une autre route l'utilise encore).
export interface DemandeDetailRelationRow {
  IdDemande: number | string;
  TypeRelation?: TypeModificationRelationKyc | null;
  CodRel?: string | null;
  NumRelExistant?: string | null;
  NomRel?: string | null;
  Prenom?: string | null;
  Cni?: string | null;
  Rang?: number | null;
  Plafond?: number | null;
}

export interface DemandesEnAttenteResult {
  Table: DemandeEnAttenteRow[];
  Table1: DemandeDetailChampRow[];
  Table2: DemandeDetailRelationRow[];
}

export const TYPES_MODIFICATION_KYC: { value: TypeModificationKyc; label: string }[] = [
  { value: 'MODIFICATION_CLIENT_COMPTE', label: 'Modification client/compte' },
  { value: 'TELEPHONE', label: 'Téléphone' },
  { value: 'NOM_COMPTE', label: 'Nom du compte' },
  { value: 'TYPE_SIGNATURE', label: 'Type de signature' },
  { value: 'IDENTITE_CLIENT', label: "Identité client" },
  { value: 'COORDONNEES_CLIENT', label: 'Coordonnées client' },
  { value: 'PIECE_IDENTITE', label: "Pièce d'identité" },
  { value: 'PROFIL_CLIENT', label: 'Profil complémentaire' },
  { value: 'EMPLOYEUR_FAMILLE', label: 'Employeur & famille' },
  { value: 'PHOTO_SIGNATURE', label: 'Photo & signature' },
  { value: 'TELEPHONE_COMPTE', label: 'Téléphone (compte)' },
];

// Champs propres à chaque ancien type de modification — utilisé pour générer
// les groupes de champs éditables de l'onglet Demande/Compte. Pas d'entrée
// utile pour MODIFICATION_CLIENT_COMPTE (type générique, jamais utilisé pour
// grouper l'affichage) : tableau vide uniquement pour satisfaire le Record.
export const CHAMPS_PAR_TYPE_MODIFICATION: Record<TypeModificationKyc, ChampKyc[]> = {
  MODIFICATION_CLIENT_COMPTE: [],
  TELEPHONE: ['TELCLI', 'TEL2'],
  // NOMCPT (2026-09-28) : n'est plus modifiable via le KYC — recalculé
  // automatiquement par le backend à partir du Nom/Prénom du client. Groupe
  // gardé (non retiré de GROUPES_DEMANDE ici, mais bien dans KycPage.tsx) pour
  // que les demandes déjà soumises avant ce changement s'affichent toujours
  // correctement en validation.
  NOM_COMPTE: ['NOMCPT'],
  // Téléphone du COMPTE (2026-09-28), distinct de TELEPHONE (TELCLI/TEL2).
  TELEPHONE_COMPTE: ['TELMOBIL'],
  // TYPSIGNATURE (texte libre legacy, jamais réellement utilisé — cf. écrans
  // de création de compte) remplacé par la vraie formule ET/OU (2026-09-25),
  // confirmée par le backend, rendue via le composant partagé Tsignature.tsx.
  // Seuls les opérateurs (TSIGN1-3) sont modifiables depuis cet écran — les
  // emplacements signataire (SIGN1-4) restent en lecture seule : leur ajout/
  // retrait passe déjà par le tableau "Signataires actuels" juste en dessous
  // (demande explicite, 2026-09-25, pour ne pas dupliquer ce mécanisme).
  TYPE_SIGNATURE: ['TSIGN1', 'TSIGN2', 'TSIGN3'],
  IDENTITE_CLIENT: ['NOMCLI', 'PRECLI', 'DATNAI', 'SITFAM', 'PROFESION'],
  // TYPADR/FAXCLI/ADRDOM ajoutés ici (2026-09-23) : ce sont bien des champs
  // "Adresse Client" sur l'écran CLIENT PHYSIQUE de référence (Tab2.tsx),
  // donc regroupés avec les coordonnées existantes plutôt qu'à part.
  COORDONNEES_CLIENT: ['ADRPOSTAL', 'RUECLI', 'NOMQTIER', 'VILCLI', 'BPCLI', 'EMAILCLI', 'TYPADR', 'FAXCLI', 'ADRDOM'],
  PIECE_IDENTITE: ['NUMCNI', 'TYPEPIECE', 'DATEDELCNI', 'NIU'],
  // Reste des 28 nouveaux champs (2026-09-23), mêmes 2 groupes que les 2
  // onglets de l'écran CLIENT PHYSIQUE de référence (Information Client /
  // Autres informations Client), moins ce qui rejoint des groupes existants.
  PROFIL_CLIENT: [
    'TITCLI', 'CLSEXE', 'ACTIONAIRE', 'LIENAI', 'CDGEST', 'CODPROSPECT',
    'CLARIS', 'CDPAYS', 'CDENAE', 'CDENUI', 'CDERES', 'CATPRO', 'RESIDE',
    'INSTRU', 'ETHNIE', 'REGMAT',
  ],
  EMPLOYEUR_FAMILLE: [
    'NBRENFANT', 'NBRPERSCHARGE', 'NOMEMP', 'MATEMP', 'ADREMP', 'NOCNPS',
    'NOMPERE', 'NOMMERE', 'REVENUCLI',
  ],
  PHOTO_SIGNATURE: ['PHOTO', 'SIGNATURE'],
};

export const LABELS_CHAMP_KYC: Record<ChampKyc, string> = {
  TELCLI: 'Téléphone principal',
  TEL2: 'Téléphone secondaire',
  NOMCPT: 'Nom du compte',
  TYPSIGNATURE: 'Type de signature',
  NOMCLI: 'Nom',
  PRECLI: 'Prénom',
  DATNAI: 'Date de naissance',
  SITFAM: 'Situation familiale',
  PROFESION: 'Profession',
  ADRPOSTAL: 'Adresse postale',
  RUECLI: 'Rue',
  NOMQTIER: 'Quartier',
  VILCLI: 'Ville',
  BPCLI: 'Boîte postale',
  EMAILCLI: 'Email',
  NUMCNI: 'N° CNI',
  TYPEPIECE: 'Type de pièce',
  DATEDELCNI: 'Date de délivrance (CNI)',
  NIU: 'NIU',
  // 28 nouveaux champs (2026-09-23)
  TITCLI: 'Titre',
  CLARIS: 'Classe de risque',
  CDPAYS: 'Nationalité',
  CDERES: 'Pays de résidence',
  RESIDE: 'Zone géographique',
  CDGEST: 'Gestionnaire',
  REGMAT: 'Régime matrimonial',
  CDENAE: 'Activité',
  CATPRO: 'Catégorie socio-professionnelle',
  ETHNIE: 'Code ethnie',
  CDENUI: 'Unité institutionnelle',
  INSTRU: "Niveau d'instruction",
  TYPADR: "Type d'adresse",
  CLSEXE: 'Genre',
  ACTIONAIRE: 'Actionnaire',
  CODPROSPECT: 'Code prospect',
  LIENAI: 'Lieu de naissance',
  FAXCLI: 'Fax',
  ADRDOM: 'Adresse domicile',
  NBRENFANT: "Nombre d'enfants",
  NBRPERSCHARGE: 'Personnes à charge',
  NOMEMP: 'Nom employeur',
  MATEMP: 'Matricule employeur',
  ADREMP: 'Adresse employeur',
  NOCNPS: 'Numéro CNPS',
  NOMPERE: 'Nom du père',
  NOMMERE: 'Nom de la mère',
  REVENUCLI: 'Revenu mensuel',
  PHOTO: 'Photo',
  SIGNATURE: 'Signature',
  SIGN1: 'Signataire 1',
  SIGN2: 'Signataire 2',
  SIGN3: 'Signataire 3',
  SIGN4: 'Signataire 4',
  SIGN5: 'Signataire 5',
  TSIGN1: 'Type de signature',
  TSIGN2: 'Type de signature',
  TSIGN3: 'Type de signature',
  TSIGN4: 'Type de signature',
  TELMOBIL: 'Téléphone (compte)',
};

// PHOTO/SIGNATURE : valeur = data URI complète d'une image (potentiellement
// volumineuse) — jamais affichée comme texte brut, toujours en aperçu image,
// et jamais incluse dans une liste/tableau qui n'en a pas besoin.
export const CHAMPS_IMAGE_KYC: ChampKyc[] = ['PHOTO', 'SIGNATURE'];
export const IMAGE_MAX_MO_KYC = 5;

// Champs CLSEXE/ACTIONAIRE : choix fixe à 2 options (radio), pas une vraie
// picklist recherchable — value1/value2 donnent le code exact à envoyer.
export const CHAMPS_RADIO_KYC: Partial<Record<ChampKyc, { value1: string; libelle1: string; value2: string; libelle2: string }>> = {
  CLSEXE: { value1: 'M', libelle1: 'Masculin', value2: 'F', libelle2: 'Féminin' },
  ACTIONAIRE: { value1: 'O', libelle1: 'Oui', value2: 'N', libelle2: 'Non' },
};

// Les 13 champs "picklist" des 28 nouveaux champs client (2026-09-23) —
// configs reprises telles quelles de l'écran CLIENT PHYSIQUE existant
// (Tab1.tsx pour les 12 premiers, table LIB filtrée par RUBRIQ ; Tab2.tsx
// pour TYPADR, table dédiée), pour réutiliser exactement les mêmes listes
// que demandé par le backend.
const RUBRIQ = (valfiltre: string): ParamsPicklist => ({ CodeAgence: '', Table: 'LIB', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '', colfiltre: 'RUBRIQ', valfiltre, Choix: 22 });

export const CHAMPS_SELECT_KYC: Partial<Record<ChampKyc, ParamsPicklist>> = {
  TITCLI: RUBRIQ('TITRESCLIENT'),
  CLARIS: RUBRIQ('CLASSERISQUE'),
  CDPAYS: RUBRIQ('PAYS'),
  CDERES: RUBRIQ('PAYS'),
  RESIDE: RUBRIQ('ZONEGEOGRAPHYQUE'),
  CDGEST: RUBRIQ('GESTIONNAIRE'),
  REGMAT: RUBRIQ('REGMAT'),
  CDENAE: RUBRIQ('ACTIVITE'),
  CATPRO: RUBRIQ('PROFESSION'),
  ETHNIE: RUBRIQ('ETHNIE'),
  CDENUI: RUBRIQ('UNITEINSTITUTIONNELLE'),
  INSTRU: RUBRIQ('NIVEAUETUDE'),
  TYPADR: { CodeAgence: '', Table: 'TYPADR', Colonne1: 'CODTYPE', Colonne2: 'LIBELL1', Colagence: '001', Choix: 22 },
  // Champs "d'origine" (parmi les 18 déjà en place avant le 2026-09-23) qui
  // se sont avérés être des picklists sur l'écran CLIENT PHYSIQUE de
  // référence, repérés en recroisant tous les champs un par un — pas juste
  // les 28 nouveaux (Tab1.tsx pour SITFAM/TYPEPIECE, Tab2.tsx pour VILCLI).
  SITFAM: RUBRIQ('SITFAM'),
  TYPEPIECE: RUBRIQ('TYPPIECE'),
  VILCLI: { CodeAgence: '', Table: 'CdVille', Colonne1: 'CdVille', Colonne2: 'Libell', Colagence: '001', Choix: 22 },
};

export const TYPES_MODIFICATION_RELATION_KYC: { value: TypeModificationRelationKyc; label: string }[] = [
  { value: 'SIGNATAIRE_AJOUT', label: 'Ajouter un signataire' },
  { value: 'SIGNATAIRE_RETRAIT', label: 'Retirer un signataire' },
  { value: 'PROCURATION_AJOUT', label: 'Ajouter une procuration' },
  { value: 'PROCURATION_RETRAIT', label: 'Retirer une procuration' },
  { value: 'SIGNATAIRE_MODIFICATION', label: "Modifier l'identité d'un signataire" },
  { value: 'PROCURATION_MODIFICATION', label: "Modifier l'identité d'un mandataire" },
];
